import React, { useState } from "react";
import { Alert, Button, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Storage } from "../../../utils/storage";
import ErrorMessageAlert from "../ErrorMessageAlert/ErrorMessageAlert";
import { ModalHeader } from "../SignInBar/SignInBarHelpers";
import "./LoginForm.module.scss";
import { styledForm } from "./LoginForm.module.scss";
import { StyledForm, TrackErrorMessage } from "./LoginFormHelpers";
import { guestServiceProvider } from "../../../services/ServiceProvider";
import { Constants } from "../../../@types/Constants";
import { checkGuestUserExist } from "../../../services/loyalty";

export const ForgotPasswordForm = ({
  onEmailSent,
  onBackToLogin,
  onCloseClick,
}: {
  onEmailSent: (message: string) => void;
  onBackToLogin: () => void;
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
  });

  const KEY_LOGIN_COOKIE = "Sonesta_login";

  const fetchSavedData = (key: any) => {
    const data = Storage.GetLocalStorageValue(KEY_LOGIN_COOKIE);
    const parsedData = data ? JSON.parse(atob(data)) : null;
    return parsedData ? parsedData[key] : null;
  };

  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
  const [isSignInSubmitting, setIsSignInSubmitting] = useState(false);
  const [isVerifiedUser, setIsVerifiedUser] = useState(false);
  const [message, setMessage] = useState("");
  const [username, setUsername] = useState(
    () => fetchSavedData("username") || ""
  );

  const handleEmailChange = (e: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setEmail(e.target.value);
  };

  const handleUsernameChange = (e: any) => {
    setUsername(e.target.value);
  };

  const OnSubmitResetPasswordLink = async (data: { email: string }, e: any) => {
    setErrorMessage("");
    setIsPasswordSubmitting(true);

    try {
      const guestService = await guestServiceProvider();
      const response = await guestService.sendResetPasswordEmail(
        data.email.trim()
      );

      if (response.createResetPasswordEmail.statusCode === "200") {
        setErrorMessage("");
        const passwordLinkMessage =
          "If there is an account with this email address, we will send you a link to set your new password.";
        setMessage(passwordLinkMessage);
        onEmailSent(passwordLinkMessage);
      } else if (
        response.createResetPasswordEmail.statusCode === "400" ||
        response.createResetPasswordEmail.statusCode === "500"
      ) {
        setErrorMessage(
          "If there is an account with this email address, we will send you a link with instructions"
        );
      } else {
        setErrorMessage("Something went wrong. Please try again later");
      }
    } catch (err) {
      console.error("Caught error in call to resetPassword:", err);
      const errorMsg =
        "We cannot reset your password at this time. Please try again later.";
      setErrorMessage(errorMsg);
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  const onSubmitOneTimeSignIn = async (data: { email: string }) => {
    setMessage("");
    setErrorMessage("");
    setIsSignInSubmitting(true);
    try {
      const email = data?.email ? data.email.trim() : "";

      if (!email) {
        setErrorMessage("Please enter a valid email address.");
        setIsSignInSubmitting(false);
        return;
      }

      const result: any = await checkGuestUserExist(email);

      if (result?.statusCode === "200") {
        const guestService = await guestServiceProvider();
        const oneTimeSignIn = await guestService.sendOneTimeSignInEmail(email);
        const oneTimeSignInStatusCode =
          oneTimeSignIn?.passwordlessLink?.statusCode || null;

        if (
          oneTimeSignInStatusCode === 200 ||
          oneTimeSignInStatusCode === 412
        ) {
          if (result?.activatedGuest === true) {
            const verifiedMessage =
              "If there is an account with this email address, we’ll send you a one-time link to log on without a password.";
            setMessage(verifiedMessage);
            onEmailSent(verifiedMessage);
          } else if (result?.activatedGuest === false) {
            const unVerifiedMessage =
              "We noticed that your account setup is incomplete. A verification email will be sent to you shortly.";
            setMessage(unVerifiedMessage);
            onEmailSent(unVerifiedMessage);
          }
          setErrorMessage("");
        } else {
          setErrorMessage("Unexpected status code. Please try again.");
        }
      } else {
        setErrorMessage(
          "If there is an account with this email address, we will send you a link with instructions"
        );
      }
    } catch (err) {
      console.error("Error in check user ===== ", err);
      setErrorMessage("An unexpected error occurred. Please try again later.");
    } finally {
      setIsSignInSubmitting(false);
    }
  };

  return (
    <>
      <ModalHeader onBackClick={onBackToLogin} onCloseClick={onCloseClick} />
      <StyledForm className={`${styledForm} form-wrapper`}>
        <Form.Group controlId="email">
          <Form.Label>Email Address</Form.Label>
          <Form.Control
            type="string"
            name="email"
            placeholder="Enter email address"
            value={username}
            onChange={handleUsernameChange}
            autoComplete="on"
            ref={register({
              required: "This field is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i,
                message: "A valid Email address is required.",
              },
            })}
            isInvalid={errors.email}
            aria-invalid={errors.email ? "true" : "false"}
          />
          <Form.Control.Feedback type="invalid">
            <TrackErrorMessage
              message={errors.email && errors.email.message}
              field="email"
            />
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Label className="smallLabel">
          Receive a password reset email link
        </Form.Label>
        <Button
          type="submit"
          id="reset-password-link"
          className="btn-primary"
          disabled={isPasswordSubmitting}
          onClick={handleSubmit(OnSubmitResetPasswordLink)}
        >
          {isPasswordSubmitting ? "Please wait..." : "Send Password Link"}
        </Button>

        <div className="or-divider-container">
          <div className="or-line"></div>
          <div className="or-text">OR</div>
          <div className="or-line"></div>
        </div>

        <Form.Label className="smallLabel">
          Sign into your account without a password
        </Form.Label>
        <Button
          type="button"
          id="send-signin-link"
          className="btn-secondary"
          onClick={handleSubmit(onSubmitOneTimeSignIn)} // Directly handle sign-in without password
          disabled={isSignInSubmitting}
        >
          {isSignInSubmitting ? "Please wait..." : "Send one-time sign-in Link"}
        </Button>
        <div className="form-error-container">
          {errorMessage && (
            <ErrorMessageAlert
              errorType={Constants.ERRORSTYPE.TRAVELPASS}
              errorSubType={Constants.ERRORSSUBTYPE.TRAVELPASS.FORGOTPASSWORD}
              message={errorMessage}
            >
              <Row>
                <Alert variant="danger" style={{ fontSize: "15px" }}>
                  {errorMessage && <p className="mb-0">{errorMessage}</p>}
                </Alert>
              </Row>
            </ErrorMessageAlert>
          )}
        </div>
      </StyledForm>
    </>
  );
};
